import { CONTACT_TYPE } from './../../constant/contact';
import { appMessageSuccess, appMessageError, appMessageLoading } from 'model/app';
import { Contact, Meeting } from 'api';
import { AppEpic } from 'model';
import { combineEpics } from 'redux-observable';
import { from, of, forkJoin, Observable } from 'rxjs';
import { switchMap, filter, map, mergeMap, startWith, catchError, throttleTime, withLatestFrom, debounceTime, take } from 'rxjs/operators';
import {
  getContactList,
  setContactList,
  getRecommendList,
  setRecommendList,
  getExternalContactList,
  setExternalContactList,
  getLabels,
  setLabels,
  getLabelList,
  getStaffLabelList,
  setLabelList,
  getSearchList,
  setSearchList,
  setContactLoading,
  editContact,
  setStaffLabelList,
  setLabelLoading,
  addContact,
  deleteContact,
  updateLabelMember,
  setLabelName,
  createLabel,
  deleteLabel,
  setDeleteLabel,
  setNewLabel,
  getOrgContactList,
  setOrgContactList,
  searchContactList,
  setSearchContactList,
  getUnionContacts,
  setUnionContacts,
  searchUnionContacts
} from '.';

import APP_MESSAGE from 'constant/message';
import { getMeetingDetailsV2, getOrderSettings } from 'model/meeting';
import { ContactV2 } from 'type/contact';
import { v4 as uuidv4 } from 'uuid';

type GetContactListAction = ReturnType<typeof getContactList>;
type SearchContactListAction = ReturnType<typeof searchContactList>;
type GetRecommendListAction = ReturnType<typeof getRecommendList>;
type GetExternalContactListAction = ReturnType<typeof getExternalContactList>;
type GetLabelsAction = ReturnType<typeof getLabels>;
type GetLabelListAction = ReturnType<typeof getLabelList>;
type GetStaffLabelListAction = ReturnType<typeof getStaffLabelList>;
type GetSearchListAction = ReturnType<typeof getSearchList>;
type editContactAction = ReturnType<typeof editContact>;
type addContactAction = ReturnType<typeof addContact>;
type deleteContactAction = ReturnType<typeof deleteContact>;
type updateLabelMemberAction = ReturnType<typeof updateLabelMember>;
type createLabelAction = ReturnType<typeof createLabel>;
type deleteLabelAction = ReturnType<typeof deleteLabel>;

const frequentUuid = uuidv4();
const orgUuid = uuidv4();
const recentlyUuid = uuidv4();

const getContactListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getContactList.match),
    mergeMap(() =>
      Contact.getContactsV2().pipe(
        mergeMap(({ data }) => of(setContactList(data), setContactLoading(false))),
        startWith(setContactLoading(true))
      )
    )
  );

const searchContactListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(searchContactList.match),
    mergeMap((action) =>
      Contact.searchContactsV2(action.payload.keyword).pipe(
        mergeMap(({ data }) => {
          const contacts = data.data;
          action.payload?.callback?.({
            id: uuidv4(),
            memberDetailResponseList: contacts
          } as unknown as ContactV2);
          return of(setSearchContactList(contacts), setContactLoading(false));
        }),
        startWith(setContactLoading(true))
      )
    )
  );

const getOrgContactListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getOrgContactList.match),
    mergeMap(() =>
      Contact.getOrgContacts().pipe(
        mergeMap(({ data }) => of(setOrgContactList(data), setContactLoading(false))),
        startWith(setContactLoading(true))
      )
    )
  );

const _getUnionContacts = (state$: Observable<any>, callback?: (contacts: ContactV2) => void) => {
  return state$.pipe(
    take(1),
    mergeMap((state) => {
      // 是否公开组织通讯录
      const publishOrganization = state.meeting.orderSettings?.publicAddressBook?.value ?? true;

      const req = forkJoin([
        Meeting.getRecentlyList(),
        Contact.getContactsV2(),
        publishOrganization ? Contact.getOrgContacts() : Promise.resolve({ data: [] })
      ]);

      return req.pipe(
        withLatestFrom(state$),
        mergeMap(([[{ data: recentlyData }, { data: contacts }, { data: orgContacts }]]) => {
          const recentlyIcon =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAALqSURBVHgB7Zu9bhNBEMf/sxdDGiwkSoo4HRWkgRJsEG3ivAAfEk2EXPAAqfIAFAZRIITCC2BEC4ppobGo6DgKSiQUN4Zwu+xscrbBdrJnry933vtJZ+99SLf735nZ29sbwhhUc7kqg2gDEnW9W0GOIaCjCB0hg11q9Npjzg9Qz5YrKopeKqCKBYSIWiTEI9rqhf1jceH309JaoPAaOe9xC8KIsHnm4UGHd4wA3PMyivaw+I2PCUUQ1NgSBO8pKR/Dn8YzxtW5QKq5VJVEe/AQqYKakILuwlMEorqAwhp8hbDBMcBfAXQsEPCcQgB4TiEAPGcJSbl4A3ThMnD2PFLl10+or2+BbgiXUPSkpKwvvroNuraN00R93IH6tANX2LvAuZVTbzxj6qCt0BXWAtDqOrKCcUFH2FtA2j5/HA7rslCjAF26Y7YkJB8FLFFfXpn/pBWaCh2fxK0XOjZcH7n/ScxFAL65ev+gvz9XEXRAFDefA+XK4JgWxBYnAoz09v63wcmhsmurcDEszyyAbW87tYqyNvn6u39Mflpmt4AJvT31dRa4dKliMgTPKQTArJRXxpenvS5lZg6CwwHpuOBke13aOHkOGGnQhN7OUsNj5vIkmNXeHsfc5gJZb3hMMQrAcwoB4DmFAPCcQgB4Ti4FMG+Xvn+AC/JpAfqNkmrddrJElmsX4HVCqYXAfohpyX8M0K7AIgy7hOrav3Oc22QoVbpHLhFPwCwXRRh7AfT6fGaYVJcEDY+xdgH14zOygvlQwhH2MUD7mMsPE6bF1MHhVyKJvhAx8Lrb6joo5eVyxWbPVuho/I9JLsCCUTwKw3MKAeA5hQB6C+EpnFPIGSNv4CmHCZV6GgFPkTLYFaXGn7Y2hTY8g7NIS41e2wRBCoL7+jeEN1DIKbRcOhRgqxdGpDb9EIFMW+P84f4wyLm0IhA1pU0DCwq7Orcxzhs+OjbKQXOpqn3knlbnisp/Wl0oSY90UrU43v1/8i8hAe8E+eoeSAAAAABJRU5ErkJggg==';
          const recentlyList = (recentlyData || []).map((item: any) => {
            return {
              ...item,
              id: `MEETING-${item.meetingNo}`,
              refId: `MEETING-${item.meetingNo}`,
              type: 'MEETING',
              isNoShowCheck: true,
              teamName: `${item?.theme}（${item?.meetingMemberList?.length || 0} 人）`,
              memberDetailResponseList: (item.meetingMemberList || []).map((menber: any) => {
                return {
                  ...menber
                };
              })
            };
          });
          const recently = {
            id: recentlyUuid,
            refId: recentlyUuid,
            teamName: '从最近会议中选择',
            nodes: recentlyList || [],
            base64Icon: recentlyIcon,
            isNoShowCheck: true
          };
          const frequentIcon =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAASDSURBVHgB7VtdTuMwEJ5x0i4S+9Aj9AbL3gBOAJwAeFxBpeUElBOwCLr7CJyA7gnoDegN6BH6ABJqE896HJKGkm6dxg604ZMQSWrL/r74Z2Y8QcgAXaxtSi/cBgk76rYJSwwE6BNCX0jvBlvPvYzfJ6A/a00KwysC2IQVBCJ2UYhj/PE8SJ7FF6NObcMjuIUlf+MGGIQIu/XDcZ9vtAD85mUY3sHqk48xEJ63xSNB8B1JeQbVIc/QU50vkC78TYl4BxWEJG9LSIF7UFEICHcEEGxAVYGwzWtAdQVQa4GAiuNTAKg4Ki+AD65BMETljESXBj5GVH6groYE2FQPmuAQ7gRQRJTNfVpvjX8lj5TJHQTBvkA8yaoiCc79r+ttPBgO42dBp7aPBGypNsABMLysEdgGkxewFTsc0xhf+O1pESThaa01ameVf3HU2Fq1LoJ9AeaQj5EWIU0+7ZKn3dfIYZN3tqeEZQFQuZq0O498DBaB1+E0+QyvdCDG4+94rNYEByJYFACViym20sGGPJjlkk9PDdsiWNoGyyGvW2IfXrXFbYIFWBCgPPJJixZFKChA+eSTli2JUECA9yOf9MCCCAsK8P7kk54UFGEBAT4O+aRHBUTIKcDHI5/0bEERzAVQFp4L8iHAcVHyMSYiwNC0jrkAArqLkmeEMtyEKVteHVkd1I8mzpIN6D4SnZuWN/YGld3+FwrAPxxfq1HQk4Hcl4Iaatx3a0dBDxyAgPr4+tRvdr/AECFRYU/sZQS14QPBeAqgLDd6TFeNBv/BAlAe5LZpWfM1AGGPFzIoAXrBfHx8kE9P93nb1OUl7ZiWz7MNNngVL0OEsQwb6iyb334zT5vJThPVNUJeQ6hZhggcT+AdIk+bi55wL2IKOxGBw16yU7/l/3yvdw1DEYoc7y/qDFkXQQCeENEOx/7yiFA0t6GIO2xVBAl0CpEF15gtwuu93UZiR9GASCER9FbXWddEed6rMHpsxr4VgeRu2hS3ldViKyaYpJyYVqAztav4/gOv2MrKbOPhiEfAdAiczxbeRJhtpvTYOhrjDt3Hb8wYKLTTIona1KnrEPn0SPAJrtJVbOcz2TwbfDVs54HD3Gn3NUsEZc/30fN24zp6pwiCe7CYz+TiZChz2M7CdJg7PR3S0FNDUi4jxwQuTofzjYSpQAaPhPDyy890GVfkGa6OxwuIwFEn7Ma/uSSv23ZyODpB7unA/+PdxDV53ZZjARhDIjzwW6Nunkr0u7YnJVyDY5SRIdJApFsmZFqhLPKM0lJkmJCJCGWSZ5SaIzRPhLLJM0pPktIivBg8MSKfoH5SNnlGGYtgNoiGiBglTxFtuFzp/wf3WWKzoAgnWWNoFsJ2gc9ESag4PgWAioMFGEBFwd8U8hcjhQ49lxnRB5VAuZyUVYKU3o2otYKeGgo9qBg4DbfWeu7pRVDF3Q5sJR4uB3DAOch8FQmgAhCc41sNEaJ85jjokmyDHLXhsBQhruyawFOdOaYjVJlG+PjC51T1faXON1r+z+oGEtVOxyk5rbcpOf8A5wgJLaJaQ2UAAAAASUVORK5CYII=';
          const frequent = {
            id: frequentUuid,
            refId: frequentUuid,
            teamName: '常用联系人',
            memberDetailResponseList: contacts,
            base64Icon: frequentIcon,
            isNoShowCheck: true
          };

          const unionContacts = {
            id: null,
            refId: null,
            nodes: [recently, frequent]
          } as unknown as ContactV2;

          if (publishOrganization) {
            const orgIcon =
              'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAORSURBVHgB7ZtbTttAFIb/YwLlkSW4KyiPFVDJrIB0BYUVAG8BVcJIFeSt7QoIK2i6AoIKqI/pCpgl8NgG4uk5Jg4o5OIwY+cy/qQovsR25p/bOWeOCX3Y+KwDamMrAsq862O2aYLQJA/nV1+o0XuSnu+8r2h/gXDGmwHmEA3UlzT2G1VSybGuAGuHepU0fmD2a3wUShM+3pxQU3ZiATo1f4H5L3yCWtTYlJbgyZ5H+Ap3Ci/4949dHSQDnm7Hte8c3OU3PR3hE9ylzK0fq3AUHgy3PK3dFYDxPThOIQAcx3kBSrCL4llFIUsIKzYHblsCKDYqdq6qL72tLLDpt9joArFdnVfhBXFk2vxMWGhtxgJwczx+7l7mxe/HZ36HIcYCLC2jjgmx+AY1GGIsQCOkO0wIG88u7AA4TiEAHKcwhWHIxqEOk21eR1BspNQwBkGoV+7/YZvYxC1FqOVtU9D6AZsyNm84hlX44UCXIwlOaqwkxzhKE7KIx0iJ6f+33gWkJtP8TkLxvYWPr9cINyo6QE4YdwH2/hrdHY3LX1VKZRnyOkS5t/Dd2xCO8Py+GWIswNUpbeJ1bA05l1ucclpngVTdyAbGLYBHcT/ZZttcpb2O+/ofbupB33M5NX/BuAXwFHabfHhEvpVpLc11D8C3QecijXPkhO0u4LdbcU7BSMSf51bwwp/n2q/fVMezJUywLYBaiNI339Iywt5jPDWmtgFsYDwGcDjsbXdnGXdpffTEAuw9zmZNAMnqyAljS/D6lGic30vBWy3s8kV7g+wASKyP0FiMRofbps4SHAZHc4/uW7gVa29I4QWfz2/zGr4MrBdpB9bXYNwF1g/1WbLNVXE5yBnq5CGEGJ/g4W983R4ywLwFcE0lH67ZM3Fw+v7uAVOJ7ZWhgYiHuFbRO5433mIG9/A7ni1qyAhzAejpz+louDOU5/yeFmMBrk9oBzNMEROE4xQCwHGMBcjSSsvj2TbiAduYEK2UrvcwbHSB3aDyFBXKC4kqs+V5BENsCCCJxxeStoKcEL/CVna77YUR8eOzzhfwYTGz3bYvMHNpt8U0CMcpBIDjiAAK7tL02Jj4CVchFoC/JpboOGkowrnXyeZowDFkCU7KHg+CbQ0Jaym4gypp7MtGLIAsVMrrpHBDhLisyYpTdxpMUtB5oWtuxwTJO5DU/uS94c6xl0iSEqskqWvv5uC1OtWZ6er9stf+AwKXRKs2YafVAAAAAElFTkSuQmCC';
            const org = {
              ...orgContacts[0],
              teamName: '组织架构',
              id: orgUuid,
              refId: orgUuid,
              base64Icon: orgIcon,
              isNoShowCheck: true
            };
            unionContacts.nodes?.push(org);
          }

          callback?.(unionContacts);

          return of(setContactList([...contacts]), setOrgContactList([...orgContacts]), setUnionContacts(unionContacts));
        })
      );
    })
  );
};

const getUnionContactsEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(getUnionContacts.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      if (state.meeting.orderSettings === null) {
        // 如果 orderSettings 不存在，先获取它
        return of(getOrderSettings()).pipe(
          mergeMap(() =>
            state$.pipe(
              filter((newState) => newState.meeting.orderSettings !== null),
              take(1),
              mergeMap(() => _getUnionContacts(state$, action.payload.callback))
            )
          )
        );
      } else {
        // 如果 orderSettings 已存在，直接获取联系人列表
        return _getUnionContacts(state$, action.payload.callback);
      }
    })
  );

const _searchUnionContacts = (state$: Observable<any>, keyword: string, callback?: (contacts: ContactV2) => void) =>
  forkJoin([Meeting.getRecentlyList(90, keyword), Contact.searchContactsV2(keyword)]).pipe(
    withLatestFrom(state$),
    mergeMap(([[{ data: recentlyData }, { data: contactsData }], state]) => {
      const recentlyList = (recentlyData || []).map((item: any) => {
        return {
          ...item,
          id: `MEETING-${item.meetingNo}`,
          refId: `MEETING-${item.meetingNo}`,
          type: 'MEETING',
          teamName: `（${item?.meetingMemberList?.length || 0}人）${item?.theme}`,
          memberDetailResponseList: (item.meetingMemberList || []).map((menber: any) => {
            return {
              ...menber
            };
          })
        };
      });
      const rid = uuidv4();
      const unionContact = {
        id: rid,
        refId: rid,
        memberDetailResponseList: contactsData.data,
        nodes: recentlyList || []
      };
      console.log('===unionContacts=>2', unionContact);
      callback?.(unionContact);
      return of(setSearchContactList(unionContact));
    })
  );

const searchUnionContactsEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(searchUnionContacts.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      return _searchUnionContacts(state$, action.payload.keyword, action.payload.callback);
    })
  );

const getRecommendListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getRecommendList.match),
    mergeMap(() =>
      from(Contact.getRecommends())
        .pipe(mergeMap((persons) => Contact.getDetailByStaffIds(persons.map((s) => s.staffId).toString())))
        .pipe(mergeMap((data) => of(setRecommendList(data.data))))
    )
  );

const getExternalContactListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getExternalContactList.match),
    mergeMap(() =>
      Contact.getContacts(2).pipe(
        mergeMap((data) => of(setExternalContactList(data), setContactLoading(false))),
        startWith(setContactLoading(true))
      )
    )
  );

const getLabelsEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getLabels.match),
    mergeMap(() => Contact.getLabelList().pipe(mergeMap((data) => of(setLabels(data.data)))))
  );

const getLabelListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getLabelList.match),
    mergeMap((action: GetLabelListAction) =>
      from(Contact.getLabelStaff(action.payload))
        .pipe(mergeMap((labelStaffs) => Contact.getDetailByStaffIds(labelStaffs.data.map((s) => s.staffId).toString())))
        .pipe(
          mergeMap((data) => of(setLabelList({ labelId: action.payload, contact: data.data }), setContactLoading(false))),
          startWith(setContactLoading(true))
        )
    )
  );

const getStaffLabelListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getStaffLabelList.match),
    mergeMap((action: GetStaffLabelListAction) =>
      Contact.getStaffLabelList(action.payload).pipe(
        mergeMap((data) => of(setStaffLabelList(data.data), setLabelLoading(false))),
        startWith(setLabelLoading(true))
      )
    )
  );

const getSearchListEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getSearchList.match),
    debounceTime(500),
    mergeMap((action: GetSearchListAction) => Contact.searchContacts(action.payload.keyword, action.payload.searchId).pipe(map((data) => setSearchList(data))))
  );

const editContactEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(editContact.match),
    mergeMap((action: editContactAction) =>
      Contact.editContact(action.payload.remark, action.payload.staffId).pipe(
        mergeMap(() =>
          of(
            appMessageSuccess(APP_MESSAGE.CONTACT_EDIT_SUCCESS),
            action.payload.type === CONTACT_TYPE.LABEL
              ? getLabelList(action.payload.labelId)
              : action.payload.type === CONTACT_TYPE.EXTERNAL
                ? getExternalContactList()
                : getContactList()
          )
        ),
        catchError(() => of(appMessageError(APP_MESSAGE.CONTACT_EDIT_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CONTACT_EDIT_PENDING))
      )
    )
  );

const addContactEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(addContact.match),
    mergeMap((action: addContactAction) =>
      Contact.addContact(action.payload.id, action.payload.staffId).pipe(
        mergeMap(() =>
          of(
            appMessageSuccess(APP_MESSAGE.CONTACT_ADD_SUCCESS),
            action.payload.meetingId
              ? getMeetingDetailsV2(action.payload.meetingId)
              : action.payload.labelId
                ? getLabelList(action.payload.labelId)
                : getContactList()
          )
        ),
        catchError(() => of(appMessageError(APP_MESSAGE.CONTACT_ADD_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CONTACT_ADD_PENGING))
      )
    )
  );

const deleteContactEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(deleteContact.match),
    mergeMap((action: deleteContactAction) =>
      Contact.deleteContact(action.payload.staffId).pipe(
        mergeMap(() =>
          of(
            appMessageSuccess(APP_MESSAGE.CONTACT_DELETE_SUCCESS),
            action.payload.type === CONTACT_TYPE.LABEL
              ? getLabelList(action.payload.labelId)
              : action.payload.type === CONTACT_TYPE.EXTERNAL
                ? getExternalContactList()
                : getContactList()
          )
        ),
        catchError(() => of(appMessageError(APP_MESSAGE.CONTACT_DELETE_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CONTACT_DELETE_PENGING))
      )
    )
  );

const updateLabelMemberEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(updateLabelMember.match),
    mergeMap((action: updateLabelMemberAction) =>
      forkJoin([
        Contact.updateLabelName(action.payload.labelId, action.payload.name),
        Contact.setLabelStaff(
          action.payload.labelId,
          action.payload.persons.map((p) => p.staffId)
        )
      ]).pipe(
        mergeMap(() =>
          of(
            appMessageSuccess(APP_MESSAGE.CONTACT_LABEL_EDIT_SUCCESS),
            getLabelList(action.payload.labelId),
            setLabelName({ labelId: action.payload.labelId, name: action.payload.name })
          )
        ),
        catchError(() => of(appMessageError(APP_MESSAGE.CONTACT_LABEL_EDIT_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CONTACT_LABEL_EDIT_PENGING))
      )
    )
  );

const createLabelEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(createLabel.match),
    mergeMap((action: createLabelAction) =>
      from(Contact.createLabel(action.payload.name))
        .pipe(
          mergeMap((data) => {
            Contact.setLabelStaff(
              data.data.id,
              action.payload.persons.map((p) => p.staffId)
            );
            return of(data.data);
          })
        )
        .pipe(
          mergeMap((data) => of(appMessageSuccess(APP_MESSAGE.CONTACT_LABEL_CREATE_SUCCESS), setNewLabel({ ...data, contact: [] }))),
          catchError(() => of(appMessageError(APP_MESSAGE.CONTACT_LABEL_CREATE_REJECTED))),
          startWith(appMessageLoading(APP_MESSAGE.CONTACT_LABEL_CREATE_PENGING))
        )
    )
  );

const deleteLabelEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(deleteLabel.match),
    mergeMap((action: deleteLabelAction) =>
      Contact.deleteLabel(action.payload).pipe(
        mergeMap(() => of(setDeleteLabel(action.payload), appMessageSuccess(APP_MESSAGE.CONTACT_LABEL_DELETE_SUCCESS))),
        catchError(() => of(appMessageError(APP_MESSAGE.CONTACT_LABEL_DELETE_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CONTACT_LABEL_DELETE_PENGING))
      )
    )
  );

export type ContactEpicActions =
  | GetContactListAction
  | SearchContactListAction
  | GetRecommendListAction
  | GetExternalContactListAction
  | GetLabelsAction
  | GetLabelListAction
  | GetStaffLabelListAction
  | GetSearchListAction
  | editContactAction
  | addContactAction
  | deleteContactAction
  | updateLabelMemberAction
  | createLabelAction
  | deleteLabelAction;

export default combineEpics(
  getContactListEpic,
  searchContactListEpic,
  getOrgContactListEpic,
  getUnionContactsEpic,
  searchUnionContactsEpic,
  getRecommendListEpic,
  getStaffLabelListEpic,
  getExternalContactListEpic,
  getLabelsEpic,
  getLabelListEpic,
  getSearchListEpic,
  editContactEpic,
  addContactEpic,
  deleteContactEpic,
  updateLabelMemberEpic,
  createLabelEpic,
  deleteLabelEpic
);
